import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription, filter } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { pageSortChange } from 'src/app/services/table';
import { Tooltip } from 'primeng/tooltip';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { ExportInfo } from 'src/app/model/export';
import { EXPORT_LIST_DEFAULT_SORT_COLUMN, EXPORT_LIST_DEFAULT_SORT_COLUMN_DIRECTION, EXPORT_LIST_PAGE_DEFAULT_SIZE, EXPORT_LIST_PAGE_SIZE_OPTIONS } from 'src/app/conf/export';
import { MtmaImportDeviceDialogComponent } from '../mtma-import-dialog/mtma-import-dialog.component';
import { MtmaExportDeviceDialogComponent } from '../mtma-export-dialog/mtma-export-dialog.component';
import { selectDeletedMtmaExportDevice, selectExportedMtmaDevice, selectExportMtmaDeviceList, selectExportMtmaDeviceListLoading, selectExportMtmaDevicePaging, selectUpdatedMtmaExportDevice } from 'src/app/store/user-file/user-file.selector';
import { selectIsImpersMode } from 'src/app/store/user/user.selector';

@Component({
	selector: 'app-mtma-import-export-list',
	templateUrl: './mtma-import-export-list.component.html',
	providers: []
})

export class MtmaImportExportListComponent implements OnInit, OnDestroy {

	exportList: ExportInfo[];
	exportList$: Observable<ExportInfo[] | undefined>;
	exportListSub: Subscription | undefined;

	exportListLoading: boolean;
	exportListLoading$: Observable<boolean | undefined>;
	exportListLoadingSub: Subscription | undefined;

	exportListPaging$: Observable<ItemsPagination | undefined>;
	exportListPagingSub: Subscription | undefined;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;
	isImpersMode: boolean;

	update$: Observable<boolean | undefined>;
	updateSub: Subscription;
	delete$: Observable<boolean | undefined>;
	deleteSub: Subscription;

	export$: Observable<boolean | undefined>;
	exportSub: Subscription;

	paging: PaginationParams = {
		size: EXPORT_LIST_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: EXPORT_LIST_DEFAULT_SORT_COLUMN,
		order: EXPORT_LIST_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSizeOptions = EXPORT_LIST_PAGE_SIZE_OPTIONS;
	pageSize = EXPORT_LIST_PAGE_DEFAULT_SIZE;
	resultLength: number;

	totalRecords = 0;

	sort: any;
	// paginator: any;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	@ViewChild(Tooltip) tooltip!: Tooltip;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		// this.getExportDeviceList();

		this.onResize();
	}

	initSelectors(): void {
		this.exportListLoading$ = this.store.select(selectExportMtmaDeviceListLoading);
		this.exportList$ = this.store.select(selectExportMtmaDeviceList);
		this.exportListPaging$ = this.store.select(selectExportMtmaDevicePaging);
		this.export$ = this.store.select(selectExportedMtmaDevice);
		this.update$ = this.store.select(selectUpdatedMtmaExportDevice);
		this.delete$ = this.store.select(selectDeletedMtmaExportDevice);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	getColumnTitle(): boolean {
		return (this.screenWidth <= this.COLUMN_TITLE_MIN_WD);
	}

	subscribeAll(): void {
		this.exportListLoadingSub = this.exportListLoading$?.subscribe(
			(exportListLoading) => {
				this.exportListLoading = exportListLoading;
			}
		);
		this.exportListSub = this.exportList$?.subscribe(
			(exportList) => {
				this.exportList = exportList;
			}
		);
		this.exportListPagingSub = this.exportListPaging$?.pipe(filter(p => !!p))
			.subscribe(
				paging => {
					if (paging !== null && paging !== undefined) {
						this.totalRecords = paging.TotalCount;
					} else {
						this.totalRecords = 0;
					}
				}
			);
		this.updateSub = this.update$?.subscribe(
			(res) => {
				if (res) {
					this.getExportDeviceList();
				}
			}
		);
		this.exportSub = this.export$?.pipe(filter(res => !!res)).subscribe(
			(res) => {
				if (res) {
					this.getExportDeviceList();
				}
			}
		);
		this.deleteSub = this.delete$?.subscribe(
			(res) => {
				if (res) {
					this.getExportDeviceList();
				}
			}
		);
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
	}

	ngOnDestroy(): void {
		this.exportListLoadingSub?.unsubscribe();
		this.exportListSub?.unsubscribe();
		this.exportListPagingSub?.unsubscribe();
		this.updateSub?.unsubscribe();
		this.exportSub?.unsubscribe();
		this.deleteSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	getExportDeviceList(): void {
		this.store.dispatch(UserFileActions.getMtmaExportDeviceList({ paging: this.paging, isImpersMode: this.isImpersMode }));
	}

	loadExportDeviceList(event: TableLazyLoadEvent) {
		this.paging = pageSortChange(this.paging, event);

		this.getExportDeviceList();
	}

	importList(exportInfo: ExportInfo): void {
		this.store.dispatch(UserFileActions.resetMtmaImportDeviceDialog());
		this.ref = this.dialogService.open(MtmaImportDeviceDialogComponent, {
			header: this.translate.instant('IMPORT_DATA_INTO_DEVICE'),
			width: '75vw',
			breakpoints: { '1500px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				exportInfo,
			}
		});
	}

	editList(exportInfo: ExportInfo): void {
		this.store.dispatch(UserFileActions.resetMtmaExportDeviceDialog());
		this.ref = this.dialogService.open(MtmaExportDeviceDialogComponent, {
			header: this.translate.instant('EDIT_NAME') + ' ' + this.translate.instant('LIST').toLowerCase(),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				exportInfo,
			}
		});
	}

	deleteList(exportInfo: ExportInfo): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('EXPORT_DATA_DEVICE_DELETE_TITLE'),
			message: this.translate.instant('EXPORT_DATA_DEVICE_DELETE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(UserFileActions.deleteMtmaExportDevice({
					exportInfo: exportInfo,
					isImpersMode: this.isImpersMode,
				}));
			},
			reject: () => {
			}
		});
	}

	downloadUrl(url: string, fileName: string) {
		const a: any = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.style = 'display: none';
		a.click();
		a.remove();
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}